// src/components/organisms/HomePage/HomePageEventSpotlight.js

import React from "react";
import { Box, Button, Typography } from "@mui/material";
import LocalMoviesOutlinedIcon from "@mui/icons-material/LocalMoviesOutlined";

/**
 * Marketing-forward spotlight for an available event.
 * Uses the event's thumbnail as a full-bleed background if provided, otherwise a gradient.
 * Single CTA to route users into /events/:id, where you’ll add PayPal + rental later.
 */
const HomePageEventSpotlight = ({
  theme,
  event,
  thumbnailUrl,
  onAction,
  ctaLabel = "View & rent",
}) => {
  const hasThumb = !!thumbnailUrl;

  const price =
    typeof event?.price === "number"
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }).format(event.price)
      : null;

  const rentalDays =
    typeof event?.rentalDays === "number" && event.rentalDays > 0
      ? event.rentalDays
      : null;

  const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
  const start = toDate(event?.startDate);
  const end = toDate(event?.endDate);

  const fmt = new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "24px",
        overflow: "hidden",
        minHeight: { xs: 240, sm: 300, md: 360 },
        display: "flex",
        alignItems: "stretch",
        backgroundColor: theme.colors.mediumPurple,
        border: `1px solid ${theme.colors.palePurple}`,
      }}
    >
      {/* Background image or fallback gradient */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: hasThumb
            ? `url(${thumbnailUrl}) center/cover no-repeat`
            : `linear-gradient(135deg, ${theme.colors.darkBlue} 0%, ${theme.colors.mediumPurple} 60%, ${theme.colors.headerPurple} 100%)`,
          filter: hasThumb ? "brightness(0.7)" : "none",
          transition: "transform 0.4s ease",
          "&:hover": { transform: hasThumb ? "scale(1.02)" : "none" },
        }}
      />

      {/* Overlay for legibility */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.65) 100%)",
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          p: { xs: 3, sm: 4, md: 5 },
          color: "white",
          fontFamily: "basic-sans",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: theme.colors.amber, letterSpacing: 1, fontWeight: 700 }}
        >
          Available Now
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!hasThumb && (
            <LocalMoviesOutlinedIcon
              sx={{ fontSize: 28, color: theme.colors.amber }}
            />
          )}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              lineHeight: 1.1,
              fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.6rem" },
            }}
          >
            {event?.title || "Featured Event"}
          </Typography>
        </Box>

        {event?.description && (
          <Typography
            variant="body1"
            sx={{ maxWidth: 900, mt: 0.5, fontWeight: 300 }}
          >
            {event.description}
          </Typography>
        )}

        {/* Badges */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mt: 1.5,
            alignItems: "center",
          }}
        >
          {price && (
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: "10px",
                bgcolor: "rgba(0,0,0,0.4)",
                border: `1px solid ${theme.colors.palePurple}`,
                fontWeight: 700,
              }}
            >
              {price}
            </Box>
          )}

          {rentalDays && (
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: "10px",
                bgcolor: "rgba(0,0,0,0.4)",
                border: `1px solid ${theme.colors.palePurple}`,
              }}
            >
              {rentalDays} day{rentalDays !== 1 ? "s" : ""} access
            </Box>
          )}

          {start && end && (
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: "10px",
                bgcolor: "rgba(0,0,0,0.4)",
                border: `1px solid ${theme.colors.palePurple}`,
              }}
            >
              Available for rent from {fmt.format(start)} to {fmt.format(end)}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 2.5, flexWrap: "wrap" }}>
          <Button
            onClick={onAction}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              px: 3,
              py: 1.2,
              backgroundColor: theme.colors.green,
              "&:hover": { backgroundColor: theme.colors.blue },
            }}
          >
            {ctaLabel}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default HomePageEventSpotlight;
